/**
 * DataUsageWarningModal Component
 *
 * Modal that warns users about uploading large files on cellular data.
 * Shows when attempting to upload files >3MB over cellular connection.
 *
 * @example
 * <DataUsageWarningModal
 *   isOpen={showWarning}
 *   fileName="photo.jpg"
 *   fileSize="4.2 MB"
 *   onContinue={handleContinue}
 *   onCancel={handleCancel}
 * />
 */

import React from 'react'
import classNames from 'classnames'
import { useResponsive } from '../../../../../shared/forms/hooks/useResponsive'

export interface DataUsageWarningModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean

  /**
   * Name of the file being uploaded
   */
  fileName: string

  /**
   * Formatted file size (e.g., "4.2 MB")
   */
  fileSize: string

  /**
   * Callback when user chooses to continue with upload
   */
  onContinue: () => void

  /**
   * Callback when user chooses to cancel and wait for WiFi
   */
  onCancel: () => void
}

/**
 * Modal component for cellular data usage warning
 *
 * Displays a warning when user attempts to upload a large file (>3MB)
 * over cellular data connection. Provides options to continue or wait
 * for WiFi.
 *
 * Mobile: Renders as full-screen bottom sheet
 * Desktop: Renders as centered modal (max-width 500px)
 */
export function DataUsageWarningModal({
  isOpen,
  fileName,
  fileSize,
  onContinue,
  onCancel
}: DataUsageWarningModalProps): JSX.Element | null {
  const { isMobile } = useResponsive()

  // Don't render if not open
  if (!isOpen) {
    return null
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-40"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        className={classNames(
          'tw-fixed tw-z-50 tw-bg-white',
          isMobile
            ? // Mobile: Bottom sheet
              'tw-bottom-0 tw-left-0 tw-right-0 tw-rounded-t-3xl tw-shadow-2xl tw-max-h-[85vh] tw-overflow-y-auto'
            : // Desktop: Centered modal
              'tw-top-1/2 tw-left-1/2 tw--translate-x-1/2 tw--translate-y-1/2 tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-md'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="data-warning-title"
        aria-describedby="data-warning-description"
      >
        {/* Modal Content */}
        <div className={classNames('tw-p-6', isMobile && 'tw-pb-8')}>
          {/* Icon and Title */}
          <div className="tw-flex tw-items-start tw-gap-4 tw-mb-4">
            {/* Warning Icon with Cellular Indicator */}
            <div className="tw-flex-shrink-0">
              <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-amber-100 tw-flex tw-items-center tw-justify-center">
                <svg
                  className="tw-w-6 tw-h-6 tw-text-amber-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Title and Description */}
            <div className="tw-flex-1">
              <h3
                id="data-warning-title"
                className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2"
              >
                Large File on Cellular Data
              </h3>
              <p
                id="data-warning-description"
                className="tw-text-sm tw-text-gray-600 tw-leading-relaxed"
              >
                You're about to upload <strong className="tw-text-gray-900">{fileName}</strong> (
                {fileSize}) over cellular data. This may use a significant amount of your data plan.
              </p>
            </div>
          </div>

          {/* Cellular Data Icon */}
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-bg-blue-50 tw-rounded-lg tw-mb-6">
            <svg
              className="tw-w-5 tw-h-5 tw-text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
              />
            </svg>
            <span className="tw-text-sm tw-font-medium tw-text-blue-900">
              Currently on Cellular Data
            </span>
          </div>

          {/* Action Buttons */}
          <div
            className={classNames(
              'tw-flex tw-gap-3',
              isMobile ? 'tw-flex-col-reverse' : 'tw-flex-row tw-justify-end'
            )}
          >
            {/* Cancel Button - "Use WiFi" */}
            <button
              type="button"
              onClick={onCancel}
              className={classNames(
                'tw-px-5 tw-py-3 tw-text-sm tw-font-medium tw-rounded-lg',
                'tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700',
                'hover:tw-bg-gray-50 active:tw-bg-gray-100',
                'focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2',
                'tw-transition-colors tw-duration-200',
                isMobile && 'tw-w-full'
              )}
              style={{ minHeight: isMobile ? '44px' : '36px' }}
            >
              Use WiFi
            </button>

            {/* Continue Button */}
            <button
              type="button"
              onClick={onContinue}
              className={classNames(
                'tw-px-5 tw-py-3 tw-text-sm tw-font-medium tw-rounded-lg',
                'tw-bg-blue-600 tw-text-white',
                'hover:tw-bg-blue-700 active:tw-bg-blue-800',
                'focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2',
                'tw-transition-colors tw-duration-200',
                isMobile && 'tw-w-full'
              )}
              style={{ minHeight: isMobile ? '44px' : '36px' }}
            >
              Continue Anyway
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
